import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useIntersectionObserver } from 'usehooks-ts';
import { BellIcon, Cross1Icon } from '@radix-ui/react-icons';
import { useCounts, useNotifications, useNovu } from '@novu/react';

import styled from 'styled-components';

import { Drawer } from '../../components/drawer/drawer';
import { breakPoint } from '../theme';
import { NotificationsList } from './notifications-list';
import { ToolBar } from '../../components/toolbar/toolbar';

export const NotificationCenter: React.FC = observer(() => {
  const novu = useNovu();
  const { hasMore, fetchMore } = useNotifications();
  const { counts } = useCounts({
    filters: [
      { read: false }, // Unread notifications
    ],
  });

  const unreadCount = counts?.[0]?.count ?? 0;

  const [isCenterOpen, setIsCenterOpen] = useState(false);

  const notificationListRef = useRef<HTMLDivElement>(null);

  const { isIntersecting, ref: lastItemRef } = useIntersectionObserver({
    root: notificationListRef.current,
    threshold: 0.1,
  });

  useEffect(() => {
    if (isIntersecting && hasMore) {
      fetchMore()
    }
  }, [isIntersecting, fetchMore, hasMore]);

  const handleMarkAllAsRead = () => {
    novu.notifications.readAll();
  };

  return (
    <>
      <BellButton
        id="bell-button"
        data-testid="notification-icon"
        data-count={unreadCount}
        onClick={() => setIsCenterOpen((isOpen) => !isOpen)}
      >
        <BellIcon />
      </BellButton>

      <NotificationDrawer
        isOpen={isCenterOpen}
        onPointerDownOutside={(e) => {
          const target = e.target as HTMLElement;

          if (target?.id === 'bell-button') {
            e.preventDefault();
          }
        }}
        onCancel={() => setIsCenterOpen(false)}
        mobileFullHeight
      >
        <ToolBar title={'Notifications'}>
          <Actions>
            <MarkReadButton onClick={handleMarkAllAsRead} data-testid="mark-as-read">
              Mark all as read
            </MarkReadButton>

            {/* <IconButtonWrapper>
              <IconButton icon={<MixerHorizontalIcon />} onClick={() => setIsCenterOpen(false)} />
            </IconButtonWrapper> */}

            {/* <IconButtonWrapper>
              <IconButton icon={<GearIcon />} onClick={() => setIsCenterOpen(false)} />
            </IconButtonWrapper> */}

            <CloseButton onClick={() => setIsCenterOpen(false)} aria-label="Close">
              <Cross1Icon />
            </CloseButton>
          </Actions>
        </ToolBar>

        <List ref={notificationListRef}>
          <MarkReadButton onClick={handleMarkAllAsRead} data-testid="mark-as-read">
            Mark all as read
          </MarkReadButton>

          <NotificationsList ref={lastItemRef} onItemClick={() => setIsCenterOpen(false)} />
        </List>
      </NotificationDrawer>
    </>
  );
});

const BellButton = styled.button`
  all: unset;
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: ${breakPoint.medium - 1}px) {
    padding: 0.8rem;
  }

  svg {
    color: var(--color-grayscale-manatee);
    width: 1.6em;
    height: 1.6rem;
    pointer-events: none;
  }

  &:not([data-count='0']) {
    &:after {
      width: 1.6rem;
      height: 1.6rem;
      content: attr(data-count);
      position: absolute;
      top: 0.6rem;
      right: 0.5rem;
      background: var(--color-primary-telemagenta, #d53075);
      border-radius: 50%;
      color: var(--color-grayscale-white, #fafafa);
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;
      font-size: 1rem;
      font-weight: 700;

      @media screen and (min-width: ${breakPoint.medium}px) {
        top: -0.2rem;
        right: -0.3rem;
      }
    }
  }

  @media screen and (min-width: ${breakPoint.medium}px) {
    min-width: 2.4rem;

    svg {
      width: 2rem;
      height: 2rem;
    }
  }
`;

const NotificationDrawer = styled(Drawer)`
  @media screen and (min-width: ${breakPoint.medium}px) {
    width: 48rem;
  }
`;

const MarkReadButton = styled.button`
  all: unset;
  color: var(--color-primary-crayola, #367bff);
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 2.4rem;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.6rem;

  @media screen and (max-width: ${breakPoint.medium - 1}px) {
    & > ${MarkReadButton} {
      display: none;
    }
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  padding: 1.6rem 2.4rem 2.4rem;
  overflow-y: auto;
  height: 100%;

  & > ${MarkReadButton} {
    display: none;
  }

  @media screen and (max-width: ${breakPoint.medium - 1}px) {
    padding: 1.6rem;

    & > ${MarkReadButton} {
      display: block;
      width: fit-content;
      align-self: end;
      margin-bottom: 0.8rem;
    }
  }
`;

const CloseButton = styled.button`
  all: unset;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 0.4rem;
  color: var(--color-grayscale-shuttle);
  box-sizing: border-box;
  border: 0.15rem solid transparent;
  cursor: pointer;

  @media screen and (min-width: ${breakPoint.medium}px) {
    display: none;
  }
`;
